"use client";

import type { FormEvent, JSX } from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SubscribeFormProps {
  source?: string;
  className?: string;
  placeholder?: string;
  buttonText?: string;
  variant?: "default" | "minimal";
  /** Force the input/button to stack even at `sm:` viewport widths — use in
   * narrow containers (e.g. a sidebar) where the `sm:` breakpoint doesn't
   * reflect the actual available width. */
  stacked?: boolean;
  /** Keep input + button grouped with a fixed input width instead of stretching. */
  compact?: boolean;
}

export function SubscribeForm({
  source = "website",
  className = "",
  placeholder = "Enter your email",
  buttonText = "Subscribe",
  variant = "default",
  stacked = false,
  compact = false,
}: SubscribeFormProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot — must stay empty
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const isDark = variant === "minimal";

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          source,
          company,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage({
          type: "success",
          text: data.message || "Successfully subscribed!",
        });
        setEmail(""); // Clear input on success
      } else {
        setMessage({
          type: "error",
          text: data.error || "Failed to subscribe. Please try again.",
        });
      }
    } catch {
      setMessage({
        type: "error",
        text: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Honeypot: hidden from users, tempting to bots. Real submissions leave it empty. */}
        <div className="absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="company-website">Company (leave blank)</label>
          <input
            id="company-website"
            type="text"
            name="company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
        <div
          className={cn(
            "flex flex-col gap-2",
            !stacked && "sm:flex-row sm:items-center",
            compact && "w-fit max-w-full"
          )}
        >
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            required
            disabled={loading}
            className={cn(
              compact ? "sm:w-52 md:w-56 sm:flex-none" : "flex-1",
              isDark && "bg-gray-800 border-gray-700 text-white placeholder:text-gray-400",
              message?.type === "error" && "border-red-500 focus-visible:ring-red-500"
            )}
          />
          <Button
            type="submit"
            disabled={loading}
            size="lg"
            variant={isDark ? "secondary" : "default"}
            className={cn(
              "whitespace-nowrap",
              isDark && "bg-white text-gray-900 hover:bg-gray-100 border-0"
            )}
          >
            {loading ? "Subscribing..." : buttonText}
          </Button>
        </div>

        {message && (
          <div
            className={cn(
              "rounded-lg px-4 py-3 text-sm",
              message.type === "success"
                ? "bg-green-50 text-green-800 border border-green-200"
                : "bg-red-50 text-red-800 border border-red-200",
              isDark && message.type === "success" && "bg-green-900/20 text-green-300 border-green-800",
              isDark && message.type === "error" && "bg-red-900/20 text-red-300 border-red-800"
            )}
            role="alert"
          >
            {message.text}
          </div>
        )}
      </form>
    </div>
  );
}
